/**
 * Intersection Tool Dialog
 *
 * Material-UI dialog for configuring intersection analysis.
 * Allows selection of source and target layers.
 *
 * **Validates: Requirements 23.1**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import {
  setIntersectionSourceLayer,
  setIntersectionTargetLayer,
  closeTool,
} from '../../store/slices/spatialAnalysisSlice';

interface IntersectionToolDialogProps {
  open: boolean;
  onExecute: () => void;
}

export const IntersectionToolDialog: React.FC<IntersectionToolDialogProps> = ({
  open,
  onExecute,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { intersectionTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.spatialAnalysis
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleSourceChange = useCallback(
    (e: any) => {
      dispatch(setIntersectionSourceLayer(e.target.value));
    },
    [dispatch]
  );

  const handleTargetChange = useCallback(
    (e: any) => {
      dispatch(setIntersectionTargetLayer(e.target.value));
    },
    [dispatch]
  );

  const polygonLayers = layers.filter(
    (l) => l.geometryType === 'Polygon' || l.geometryType === 'MultiPolygon'
  );

  const canExecute =
    intersectionTool.sourceLayerId &&
    intersectionTool.targetLayerId &&
    intersectionTool.sourceLayerId !== intersectionTool.targetLayerId &&
    !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Intersection Analysis</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Compute the geometric intersection of two polygon layers. The result contains only the
            overlapping portions with attributes from both layers.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="intersection-source-label">Source Layer</InputLabel>
            <Select
              labelId="intersection-source-label"
              value={intersectionTool.sourceLayerId || ''}
              onChange={handleSourceChange}
              label="Source Layer"
            >
              {polygonLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="intersection-target-label">Target Layer</InputLabel>
            <Select
              labelId="intersection-target-label"
              value={intersectionTool.targetLayerId || ''}
              onChange={handleTargetChange}
              label="Target Layer"
            >
              {polygonLayers
                .filter((l) => l.id !== intersectionTool.sourceLayerId)
                .map((layer) => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {layer.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && (
            <Alert severity={message.includes('No intersections') ? 'info' : 'success'}>
              {message}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Compute Intersection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntersectionToolDialog;
